import type { WalletAccessResponse } from '@/api/wallet/wallet-access';

import type { WalletUnlockGrant } from './wallet-unlock-session';

export enum PrivyBindingStatus {
  Pending = 'pending',
  SignedOut = 'signed-out',
  Mismatched = 'mismatched',
  Bound = 'bound',
}

export type PrivyBinding =
  | { status: PrivyBindingStatus.Pending }
  | { status: PrivyBindingStatus.SignedOut }
  | { status: PrivyBindingStatus.Mismatched }
  | { status: PrivyBindingStatus.Bound; privyUserId: string };

export enum GrantIdentity {
  Unverified = 'unverified',
  Verified = 'verified',
  SignedOut = 'signed-out',
  Conflicting = 'conflicting',
}

export type PrivyUserState = {
  ready: boolean;
  authenticated: boolean;
  user: { id: string } | null;
};

export const resolvePrivyBinding = (
  privy: PrivyUserState,
  access: WalletAccessResponse | undefined,
): PrivyBinding => {
  // Until Privy and the wallet record have loaded, identity is unknown: never unlock and never lock on it.
  if (!privy.ready || !access) {
    return { status: PrivyBindingStatus.Pending };
  }

  if (!privy.authenticated || !privy.user) {
    return { status: PrivyBindingStatus.SignedOut };
  }

  if (privy.user.id !== access.privyUserId) {
    return { status: PrivyBindingStatus.Mismatched };
  }

  return { status: PrivyBindingStatus.Bound, privyUserId: privy.user.id };
};

export const boundPrivyUserId = (binding: PrivyBinding): string | null =>
  binding.status === PrivyBindingStatus.Bound ? binding.privyUserId : null;

export const checkGrantIdentity = (
  binding: PrivyBinding,
  grant: WalletUnlockGrant,
): GrantIdentity => {
  switch (binding.status) {
    case PrivyBindingStatus.Pending:
      return GrantIdentity.Unverified;
    // A signed-out Privy state may be stale in this tab (the sign-in happened in another), so it locks
    // only this tab; a different Privy user is a real conflict and locks every tab.
    case PrivyBindingStatus.SignedOut:
      return GrantIdentity.SignedOut;
    case PrivyBindingStatus.Mismatched:
      return GrantIdentity.Conflicting;
    case PrivyBindingStatus.Bound:
      return binding.privyUserId === grant.privyUserId
        ? GrantIdentity.Verified
        : GrantIdentity.Conflicting;
  }
};
